import { CommonModule } from '@angular/common';
import { Component, computed, effect, input, output, signal } from '@angular/core';
import { ZoomContainerComponent } from '@app/components/zoom/zoom-container/zoom-container.component';
import { SystemImage } from '../hgs-location.component';

@Component({
  selector: 'app-hgs-svg-image',
  template: `
    @if (system(); as sys) {
      <div [attr.class]="'border border-slate-200 overflow-hidden rounded-lg' + imgClass()">
        <app-zoom-container class="h-full w-full" [initialScale]="1" [minScale]="0.5" [maxScale]="3">
          <svg [attr.viewBox]="'0 0 ' + imageWidth() + ' ' + imageHeight()" class="h-auto w-full" xmlns="http://www.w3.org/2000/svg">
            <image
              [attr.href]="sys.imagePlacementUrl"
              [attr.width]="imageWidth()"
              [attr.height]="imageHeight()"
              x="0"
              y="0"
              preserveAspectRatio="none" />

            <!-- Component Boxes -->
            @for (component of sys.components; track component.componentId) {
              @if (component.xAbs && component.yAbs) {
                <rect
                  [attr.x]="component.xAbs"
                  [attr.y]="component.yAbs"
                  [attr.width]="boxWidth()"
                  [attr.height]="boxHeight()"
                  [attr.stroke-width]="2"
                  (mouseenter)="onHover.emit(component.componentId)"
                  (mouseleave)="onHover.emit(null)"
                  [attr.class]="rectClass(component.componentId, 'cursor-pointer')">
                  <title>({{ highlightedComponentId() }}) {{ component.componentId }} - {{ component.name?.title }}</title>
                </rect>
                <text
                  [attr.x]="parseFloat(component.xAbs) + boxWidth() / 2"
                  [attr.y]="parseFloat(component.yAbs) + boxHeight() / 2"
                  text-anchor="middle"
                  dominant-baseline="middle"
                  [attr.class]="
                    highlightedComponentId() === component.componentId
                      ? 'pointer-events-none fill-blue-800 text-xs font-bold'
                      : 'pointer-events-none fill-blue-600 text-xs font-bold'
                  "
                  style="font-size: 12px;">
                  {{ component.componentId }}
                </text>
              }
            }
          </svg>
        </app-zoom-container>
      </div>
    }
  `,
  imports: [CommonModule, ZoomContainerComponent],
  standalone: true,
})
export class HgsSvgImageComponent {
  imgClass = input<string>();
  system = input<SystemImage>();
  highlightedComponentId = input<string | null>(null);
  onHover = output<string | null>();

  imageWidth = signal<number>(800);
  imageHeight = signal<number>(600);
  boxWidth = computed(() => this.imageWidth() * 0.09);
  boxHeight = computed(() => this.imageHeight() * 0.08);

  rectClass(compId: string, baseClasses: string): string {
    const highlightClassses = this.highlightedComponentId() === compId ? 'stroke-blue-800 fill-blue-100/50' : 'stroke-blue-600 fill-transparent';
    return `${baseClasses} ${highlightClassses}`;
  }

  onChange = effect(() => {
    const sys = this.system();
    if (sys?.imagePlacementUrl) {
      this._loadImageDimensions(sys.imagePlacementUrl);
    }
  });

  private _loadImageDimensions(url: string): void {
    const img = new Image();
    img.onload = () => {
      this.imageWidth.set(img.width);
      this.imageHeight.set(img.height);
    };
    img.src = url;
  }

  parseFloat(value: string): number {
    return parseFloat(value);
  }
}
